// GI31 엑셀 업로드 — 1.0 파리티(TOOL_IMP). 서버 API 없이 클라이언트에서 처리한다.
// 다운로드는 BE 비밀번호 zip API 로 옮겼다(LOGISPRD-15797).
import ExcelJS from 'exceljs'
import { EXPORT_COLUMNS, IMPORTABLE_KEYS, MESSAGES } from '@/pages/outbound/generalOutbound/components/consts'

const titleToKey = new Map(EXPORT_COLUMNS.map((c) => [c.title, c.key]))

/**
 * 셀 값을 문자열로 읽는다.
 *
 * ExcelJS 의 셀 값은 원시값 말고도 하이퍼링크(`{text}`)·서식문자열(`{richText}`)·수식(`{formula, result}`)
 * 형태가 온다. 객체를 그냥 String() 하면 전부 "[object Object]" 가 되어 조용히 그 값으로 저장된다.
 * 사람이 양식을 손보다 서식이 끼거나 수식을 남기면 실제로 걸린다.
 */
const cellText = (raw: unknown): string => {
  if (raw == null) return ''
  if (raw instanceof Date) return raw.toISOString().slice(0, 10)
  if (typeof raw !== 'object') return String(raw)

  const cell = raw as { text?: unknown; richText?: { text?: unknown }[]; result?: unknown; error?: unknown }
  if (Array.isArray(cell.richText)) return cell.richText.map((part) => String(part?.text ?? '')).join('')
  if (cell.text != null) return String(cell.text)
  // 수식 셀은 계산 결과를 쓴다. 결과가 오류면 값이 없는 것으로 본다.
  if (cell.result != null) return cellText(cell.result)
  if (cell.error != null) return ''
  return ''
}

/** 숫자로 저장해야 할 컬럼. 나머지는 문자열 그대로 둔다(주문번호 앞 0 이 사라지면 다른 주문이 된다). */
const NUMERIC_KEYS = new Set(['ORD_QTY', 'OPT_CD'])

export type ImportedRow = Record<string, string | number>

/** 다운로드 양식과 같은 1행 한글 헤더를 읽어 컬럼 키로 매핑한다. */
export const parseGeneralOutboundExcel = async (file: File): Promise<ImportedRow[]> => {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  const ws = wb.worksheets[0]
  if (!ws) return []

  const headerKeys: (string | null)[] = []
  ws.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
    // 데이터 셀과 같은 규칙으로 읽는다. 헤더에 수식이나 리치텍스트가 들어오면 `String(cell.value)` 이
    // "[object Object]" 가 되어 그 열이 통째로 안 잡힌다.
    const key = titleToKey.get(cellText(cell.value).trim()) ?? null
    headerKeys[col] = key && IMPORTABLE_KEYS.has(key) ? key : null
  })
  if (!headerKeys.some(Boolean)) {
    throw new Error('헤더를 인식할 수 없습니다. 엑셀 다운로드 양식과 동일한 1행 헤더가 필요합니다.')
  }

  const rows: ImportedRow[] = []
  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const item: ImportedRow = {}
    let hasValue = false
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headerKeys[col]
      if (!key) return
      const text = cellText(cell.value)
      if (!text.trim()) return
      hasValue = true
      if (!NUMERIC_KEYS.has(key)) {
        item[key] = text.trim()
        return
      }
      // 숫자로 안 읽히면 키를 넣지 않는다. `|| 0` 으로 채우면 옵션코드가 0 이 되어 미입력 검사도,
      // `toOptionCode` 의 "숫자로 안 읽히면 미입력" 규칙도 함께 무력해진다 — 그대로 저장된다.
      const parsed = Number(text)
      if (Number.isFinite(parsed)) item[key] = parsed
    })
    if (!hasValue) return
    rows.push(item)
  })
  return rows
}
